import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ALLROLES, UnitEmployment } from '../../models/unitEmployment';
import { FormBuilder, FormGroup } from '@angular/forms';
import { User } from '../../../lib/classes/user';
import { take } from 'rxjs/operators';
import {
  ActionTypes,
  DeleteUnitEmployment,
  SelectUnitEmployment,
  UnitEmploymentError,
  UpdateUnitEmployment,
  UpdateUnitEmploymentSuccess,
} from '../../actions/unit';
import { Store } from '@ngrx/store';
import * as fromUnits from '../../reducers';
import { Actions, ofType } from '@ngrx/effects';
import { Subscription } from 'rxjs';

export interface DialogData {
  unitEmployment: UnitEmployment;
}

@Component({
  selector: 'mj-unit-user-edit',
  templateUrl: 'unit-user-edit.component.html',
  styleUrls: ['unit-user-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitUserEditComponent implements OnInit, OnDestroy {
  unitEmployment: UnitEmployment;
  unitEmploymentForm: FormGroup;
  success$: Subscription;
  deleted$: Subscription;
  errors$: Subscription;
  roles = ALLROLES;

  constructor(
    public dialogRef: MatDialogRef<UnitUserEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private _fb: FormBuilder,
    private _snackbar: MatSnackBar,
    private store: Store<fromUnits.State>,
    private actions$: Actions
  ) {
    this.unitEmployment = data.unitEmployment;
    this._initForm();
    this._fillForm(this.unitEmployment);
  }

  ngOnDestroy() {
    this.success$.unsubscribe();
    this.deleted$.unsubscribe();
    this.errors$.unsubscribe();
  }

  ngOnInit() {
    this.success$ = this.actions$
      .pipe(ofType(ActionTypes.UpdateUnitEmploymentSuccess))
      .subscribe((payload: UpdateUnitEmploymentSuccess) => {
        this.store.dispatch(new SelectUnitEmployment({ unitEmployment: null }));
        this._snackbar.open('User updated', null, {
          duration: 3000,
        });
        this.dialogRef.close();
      });
    this.deleted$ = this.actions$
      .pipe(ofType(ActionTypes.DeleteUnitEmploymentSuccess))
      .subscribe(null_value => {
        this.store.dispatch(new SelectUnitEmployment({ unitEmployment: null }));
        this._snackbar.open('User deleted', null, {
          duration: 3000,
        });
        this.dialogRef.close();
      });
    this.errors$ = this.actions$
      .pipe(ofType(ActionTypes.UnitEmploymentError))
      .subscribe((ueError: UnitEmploymentError) => {
        this._snackbar.open(ueError.payload.error, null, {
          duration: 3000,
        });
      });
  }

  private _initForm() {
    this.unitEmploymentForm = this._fb.group(
      {
        id: '',
        unit: '',
        role: null,
        user: this._fb.group({
          username: '',
          id: '',
          first_name: '',
          last_name: '',
          user_profile: this._fb.group({
            // make a nested group
            id: '',
            phone: '',
            profile_picture_media_id: null,
            language: 'en',
          }),
        }),
      },
      { updateOn: 'blur' }
    );
  }

  private _fillForm(unitEmployment: UnitEmployment) {
    this.unitEmploymentForm.patchValue({
      id: unitEmployment.id,
      role: unitEmployment.role,
      unit: unitEmployment.unit,
      user: {
        username: unitEmployment.user.username,
        id: unitEmployment.user.id,
        first_name: unitEmployment.user.first_name,
        last_name: unitEmployment.user.last_name,
        user_profile: {
          id: unitEmployment.user.user_profile.id,
          phone: unitEmployment.user.user_profile.phone,
          language: unitEmployment.user.user_profile.language,
          profile_picture_media_id:
            unitEmployment.user.user_profile.profile_picture_media_id,
          profile_picture_thumbnail:
            unitEmployment.user.user_profile.profile_picture_thumbnail,
        },
      },
    });
  }

  public updateImageAction(user: User) {
    if (user && user.id) {
      this.unitEmploymentForm.patchValue({
        user: {
          user_profile: {
            profile_picture_thumbnail:
              user.user_profile.profile_picture_thumbnail,
            profile_picture_media_id:
              user.user_profile.profile_picture_media_id,
          },
        },
      });
    }
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  onSaveClick() {
    this.store.dispatch(
      new UpdateUnitEmployment({
        unitEmployment: this.unitEmploymentForm.value,
      })
    );
  }

  deleteUser() {
    this.store.dispatch(
      new DeleteUnitEmployment({
        unitEmployment: this.unitEmploymentForm.value,
      })
    );
  }

  promptDeleteUser() {
    this._snackbar
      .open('Are you sure you want to delete this user?', 'Delete', {
        panelClass: ['snackbar-50', 'snackbar-danger'],
        duration: 6000,
      })
      .afterDismissed()
      .pipe(take(1))
      .subscribe(ev => {
        if (ev.dismissedByAction) {
          this.deleteUser();
        }
      });
  }
}
